/**
 * The legal sources a stage's turns relied on, shared by both order simulators. The old chat pages
 * showed them under each message (SimSources); the run-through page keeps them per stage instead.
 * Mirrors the `sources` event of both simulators' contract.py (a landed stage's cited authorities).
 */

export const SOURCE_KINDS = ["judgment", "provision", "circular", "other"] as const
export type SourceKind = (typeof SOURCE_KINDS)[number]

/** One authority a turn retrieved and cited. `paras` are the paragraph numbers that were read and cited. */
export interface SourceRef {
  kind: SourceKind
  title: string
  citation?: string
  court?: string
  date?: string
  url?: string
  paras?: number[]
  verified: boolean
}

/** A `sources` frame: the authorities one turn relied on, under the stage it ran in. */
export interface SourcesPayload<S extends string = string> { stage: S; sources: SourceRef[] }

const sourceKey = (s: SourceRef) => `${s.url ?? ""}|${s.title}|${s.citation ?? ""}`.toLowerCase()

/** Add the new sources after the ones already kept, skipping any authority that is already listed. */
export function mergeSources(prev: SourceRef[] | undefined, next: SourceRef[] | undefined): SourceRef[] {
  const kept = prev ?? []
  const seen = new Set(kept.map(sourceKey))
  const out = [...kept]
  for (const s of next ?? []) {
    const key = sourceKey(s)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(s)
  }
  return out
}

/** Only http(s) links are rendered as links; anything else is shown as plain text. */
export const isWebUrl = (url: string | undefined): url is string => !!url && /^https?:\/\//i.test(url)
